import { ArrowLeftIcon, DeviceTabletIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../../../components/Sidebar/Sidebar";
import { apiV3 } from "../../../../utils/helpers/api";
import { useToast } from "../../../../components/Toastr/Toastr";
import {
  APP_TYPES,
  locationHint,
  usesSelfServeLocation,
} from "../tabletAppTypes";

export default function AddTabletV3() {
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [appType, setAppType] = useState<string>("SelfServe");
  const [locationId, setLocationId] = useState<string>("");
  const [selfServeLocations, setSelfServeLocations] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLocationOptions();
  }, []);

  const fetchLocationOptions = async () => {
    setLoading(true);
    try {
      const [selfServeLocationsRes, locationsRes] = await Promise.all([
        apiV3.getSelfServeLocations(1, 100, "", "", "ASC", "name"),
        apiV3.getLocations(1, 100, "", "", "ASC", "name"),
      ]);
      setSelfServeLocations(selfServeLocationsRes.data.data);
      setLocations(locationsRes.data.data);
    } catch (error) {
      console.error("Error occurred while fetching location options - ", error);
      showErrorToast();
    }
    setLoading(false);
  };

  const updateAppType = (newAppType: string) => {
    // The location lists differ per app type, so a previous choice is invalid
    setAppType(newAppType);
    setLocationId("");
  };

  const locationOptions = usesSelfServeLocation(appType) ? selfServeLocations : locations;

  const addTablet = async () => {
    setLoading(true);
    try {
      await apiV3.createDevice({
        deviceIdentifier: name,
        appType,
        ...(usesSelfServeLocation(appType)
          ? { selfServeLocationId: locationId }
          : { locationId }),
      });
      showSuccessToast("Successfully created tablet");
      navigate("/v3/tablets");
    } catch (error: any) {
      console.error("Error occurred while creating tablet - ", error);
      if (error?.response?.status === 409) {
        showErrorToast("A tablet with that name already exists");
      } else {
        showErrorToast();
      }
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">
          <div className="py-4">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h1 className="text-2xl font-bold text-gray-900 flex flex-row items-center">
                    <DeviceTabletIcon className="h-6 w-6 mr-1.5" />
                    Add Tablet
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Register a tablet and lock it to a location. The name must match the tablet's
                    device name (MEL-SerialNo).
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                  <button
                    onClick={() => navigate(-1)}
                    type="button"
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
                  >
                    <ArrowLeftIcon className="h-5 w-5 mr-1.5" />
                    Back
                  </button>
                </div>
              </div>
              <div className="mt-8 flex flex-col max-w-screen-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div className="space-y-8">
                    <div>
                      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-4">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Name
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <input
                              type="text"
                              name="name"
                              id="name"
                              placeholder="MEL-SerialNo"
                              disabled={loading}
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-4">
                          <label
                            htmlFor="appType"
                            className="block text-sm font-medium text-gray-700"
                          >
                            App
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <select
                              id="appType"
                              name="appType"
                              disabled={loading}
                              value={appType}
                              onChange={(e) => updateAppType(e.target.value)}
                              className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                            >
                              {APP_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="sm:col-span-4">
                          <label
                            htmlFor="location"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Location
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <select
                              id="location"
                              name="location"
                              disabled={loading}
                              value={locationId}
                              onChange={(e) => setLocationId(e.target.value)}
                              className="flex-1 focus:ring-amber-500 focus:border-amber-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300 disabled:bg-gray-100"
                            >
                              <option value="">Select a location</option>
                              {locationOptions.map((location) => (
                                <option key={location.id} value={location.id}>
                                  {location.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">{locationHint(appType)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-5">
                    <div className="flex justify-start items-center">
                      <button
                        onClick={() => navigate("/v3/tablets")}
                        type="button"
                        disabled={loading}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        onClick={() => addTablet()}
                        disabled={loading || !name || !locationId}
                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-60"
                      >
                        Create
                      </button>
                      {(!name || !locationId) && (
                        <div className="ml-3 text-red-500 text-sm font-semibold border-none">
                          Please enter all the required fields to create a tablet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
